import { Suspense, lazy } from 'react';
import { Icon, Reveal, type IconName } from '@byaft/ui';
import { usePageMeta } from '@byaft/ui/seo';
import { CYBER_URL, TOOLS_URL } from '../siteConfig';

// The scroll-film carries three.js + Lenis — load it on demand.
const HomeFilm = lazy(() => import('../features/film/HomeFilm'));

function FilmFallback() {
  return <div className="min-h-[100svh]" aria-hidden="true" />;
}

const products: { name: string; href: string; icon: IconName; body: string; cta: string }[] = [
  {
    name: 'BYAFT Tools',
    href: TOOLS_URL,
    icon: 'layout',
    body: 'A workspace of free, browser-based tools — CV builder, invoice generator, PDF & image tools, QR codes, calculators and more. Nothing to install, nothing uploaded.',
    cta: 'Open the tools',
  },
  {
    name: 'BYAFT Cyber',
    href: CYBER_URL,
    icon: 'shield',
    body: 'Security reviews for apps built with AI — catching the vulnerabilities AI tends to leave behind. Launching soon; join the waitlist.',
    cta: 'See what’s coming',
  },
];

export function Home() {
  usePageMeta({
    title: 'BYAFT',
    seoTitle: 'BYAFT — Software, built with energy.',
    seoDescription:
      "BYAFT builds fast, free, browser-based software for everyday digital work — and security for the AI era. What BYAFT is, and where it's going.",
  });

  return (
    <>
      {/* Crawlable heading + intro. The hero is a scroll-scrubbed WebGL film
          (not prerendered), so this gives crawlers a clear H1 + copy. */}
      <header className="sr-only">
        <h1>BYAFT — software, built with energy</h1>
        <p>
          BYAFT is a studio building fast, free, browser-based software for everyday digital work —
          and security for the AI era. Free tools today, with security for AI-built apps next. No
          accounts, nothing to install, and your work stays on your device.
        </p>
      </header>

      <Suspense fallback={<FilmFallback />}>
        <HomeFilm />
      </Suspense>

      {/* Products */}
      <section className="px-margin-safe pb-stack-xl pt-stack-lg" aria-labelledby="products-heading">
        <div className="container-byaft">
          <Reveal>
            <h2 id="products-heading" className="text-headline-xl text-on-background">
              Two sides of BYAFT
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-5 md:grid-cols-2">
            {products.map((product, i) => (
              <Reveal key={product.name} delay={i * 90} className="h-full">
                <a
                  href={product.href}
                  className="group flex h-full flex-col rounded-2xl border border-glass-border bg-surface-container-high p-7 transition-all duration-300 hover:-translate-y-1 hover:border-primary-container/50 hover:shadow-glow-orange"
                >
                  <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-surface text-primary-container transition-transform duration-300 group-hover:scale-110">
                    <Icon name={product.icon} size={24} />
                  </div>
                  <h3 className="mb-2 font-display text-2xl font-bold text-on-background">
                    {product.name}
                  </h3>
                  <p className="mb-7 flex-grow text-body-md text-on-surface-variant">{product.body}</p>
                  <span className="inline-flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wide text-primary-container transition-colors group-hover:text-primary">
                    {product.cta}
                    <Icon
                      name="arrow-right"
                      size={16}
                      className="transition-transform duration-300 group-hover:translate-x-1"
                    />
                  </span>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
